import {
  colors,
  Icon,
  SkeletonText,
  type IconName,
} from '@dev-spendesk/grapes';
import classNames from 'classnames';
import { useMemo } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';

import { useAccountingSoftwareName } from '../../apis';
import { type SyncMode } from '../../apis/useSetSyncSettings';

type Props = {
  fieldName: string | undefined;
  syncMode?: SyncMode;
  isLoading: boolean;
  isSyncOperationInProgress?: boolean;
  isWrapped?: boolean;
};

type StatusProps = {
  iconName: IconName;
  label: string;
  isSecondary?: boolean;
};

const Status = ({ iconName, label, isSecondary }: StatusProps) => {
  return (
    <div
      className={classNames(`body-m flex items-center`, {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'text-content-secondary-bg-secondary': isSecondary,
      })}
    >
      <Icon
        size="m"
        name={iconName}
        color={
          isSecondary ? colors.contentDecorativeIcon : colors.contentPrimary
        }
      />
      <div className="body-m ml-8">{label}</div>
    </div>
  );
};

export const CurrentMapping = ({
  fieldName,
  syncMode,
  isLoading,
  isSyncOperationInProgress,
  isWrapped,
}: Props) => {
  const { t } = useTranslation('global');
  const { accountingSoftwareName } = useAccountingSoftwareName();

  const isMapped = !!fieldName && syncMode !== 'none';

  const iconName = useMemo((): IconName | null => {
    switch (syncMode) {
      case 'pull':
        return 'arrow-uturn-left-down';
      case 'pullAndMap':
        return 'link';
      case 'none':
        return 'hexagone-cross';
      default:
        return null;
    }
  }, [syncMode]);

  const syncModeText = useMemo(() => {
    switch (syncMode) {
      case 'pull':
        return t(
          'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.mapping.pull',
          { integration: accountingSoftwareName },
        );
      case 'pullAndMap':
        return t(
          'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.mapping.pullAndMap',
          { integration: accountingSoftwareName },
        );
      case 'none':
        return t(
          'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.mapping.none',
        );
      default:
        return null;
    }
  }, [syncMode, accountingSoftwareName]);

  if (isLoading) {
    return (
      <div className="mb-8 px-8">
        <SkeletonText />
      </div>
    );
  }

  return (
    <div
      className={classNames('my-8 flex gap-16 pl-16', {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'rounded-12 bg-background-secondary-default py-8': isWrapped,
      })}
    >
      <div className="flex gap-8">
        <div className="border-border-default rounded-bl-8 mt-4 h-12 w-12 border-2 border-t-0 border-r-0" />
        {isSyncOperationInProgress && (
          <Status
            iconName="clock-filled"
            label={t(
              'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.syncOperationInProgress',
              {
                integration: accountingSoftwareName,
              },
            )}
            isSecondary
          />
        )}
        {!isSyncOperationInProgress && isMapped && (
          <Status
            iconName="link"
            label={t(
              'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.currentMapping',
              {
                integration: accountingSoftwareName,
                fieldName,
              },
            )}
          />
        )}
        {!isSyncOperationInProgress && !isMapped && (
          <Status
            iconName="hexagone-cross"
            label={t(
              'bookkeep.integrations.settings.analyticalFieldMapping.configurationPage.notMapped',
              {
                integration: accountingSoftwareName,
              },
            )}
            isSecondary
          />
        )}
      </div>

      {!isSyncOperationInProgress && syncMode && syncMode !== 'none' && (
        <div className="flex items-center gap-8">
          {iconName && (
            <Icon name={iconName} color={colors.contentSecondaryBgSecondary} />
          )}
          {syncModeText && <span>{syncModeText}</span>}
        </div>
      )}

      {!isSyncOperationInProgress && isMapped && !syncMode && (
        <div className="flex items-center gap-8">
          <Icon name="triangle-warning" color={colors.contentAlertDefault} />
          <p className="text-content-secondary-bg-secondary">
            {t('costCenters.syncNotConfigured')}
          </p>
        </div>
      )}
    </div>
  );
};
